import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [creditPacks, setCreditPacks] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingUser, setEditingUser] = useState(null);
  const [newCredits, setNewCredits] = useState('');
  
  // Credit pack editing
  const [editingPack, setEditingPack] = useState(null);
  const [packForm, setPackForm] = useState({ packId: '', name: '', credits: '', price: '', description: '', popular: false, active: true, sortOrder: 0 });
  const [showPackForm, setShowPackForm] = useState(false);
  
  // Promo code editing
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({ code: '', credits: '', maxUses: '', expiresAt: '', description: '', active: true });
  const [showPromoForm, setShowPromoForm] = useState(false);

  useEffect(() => {
    // Redirect if not admin
    if (user && !user.isAdmin) {
      navigate('/dashboard');
      return;
    }
    
    if (user?.isAdmin) {
      loadData();
    }
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, messagesRes, usersRes, packsRes, promosRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/messages'),
        api.get('/admin/users'),
        api.get('/admin/credit-packs'),
        api.get('/admin/promo-codes')
      ]);
      setStats(statsRes.data);
      setMessages(messagesRes.data.messages);
      setUsers(usersRes.data.users);
      setCreditPacks(packsRes.data.packs);
      setPromoCodes(promosRes.data.codes);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `£${(amount || 0).toFixed(2)}`;
  };

  const handleEditCredits = (userToEdit) => {
    setEditingUser(userToEdit);
    setNewCredits(userToEdit.credits.toString());
  };

  const handleSaveCredits = async () => {
    if (!editingUser) return;
    
    const credits = parseInt(newCredits);
    if (isNaN(credits) || credits < 0) {
      toast.error('Please enter a valid number of credits');
      return;
    }

    try {
      await api.put(`/admin/users/${editingUser.id}/credits`, { credits });
      toast.success(`Updated ${editingUser.name}'s credits to ${credits}`);
      
      // Refresh user list
      setUsers(users.map(u => 
        u.id === editingUser.id ? { ...u, credits } : u
      ));
      setEditingUser(null);
      setNewCredits('');
    } catch (error) {
      toast.error('Failed to update credits');
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setNewCredits('');
  };

  const handleToggleSenderId = async (u) => {
    const newValue = !u.senderIdUnlocked;
    try {
      await api.put(`/admin/users/${u.id}/sender-id-unlocked`, { unlocked: newValue });
      setUsers(users.map(user =>
        user.id === u.id ? { ...user, senderIdUnlocked: newValue } : user
      ));
      toast.success(`Custom sender ID ${newValue ? 'enabled' : 'disabled'} for ${u.name}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update custom sender ID');
    }
  };

  const truncateMessage = (content, maxLength = 50) => {
    if (!content) return '';
    return content.length > maxLength ? content.substring(0, maxLength) + '...' : content;
  };

  // ==================== CREDIT PACK MANAGEMENT ====================
  const handleAddPack = () => {
    setEditingPack(null);
    setPackForm({ packId: '', name: '', credits: '', price: '', description: '', popular: false, active: true, sortOrder: 0 });
    setShowPackForm(true);
  };

  const handleEditPack = (pack) => {
    setEditingPack(pack);
    setPackForm({
      packId: pack.pack_id,
      name: pack.name,
      credits: pack.credits.toString(),
      price: pack.price.toString(),
      description: pack.description || '',
      popular: !!pack.popular,
      active: !!pack.active,
      sortOrder: pack.sort_order || 0
    });
    setShowPackForm(true);
  };

  const handleSavePack = async () => {
    if (!packForm.name || !packForm.credits || !packForm.price) {
      toast.error('Name, credits, and price are required');
      return;
    }

    try {
      if (editingPack) {
        await api.put(`/admin/credit-packs/${editingPack.id}`, {
          name: packForm.name,
          credits: parseInt(packForm.credits),
          price: parseFloat(packForm.price),
          description: packForm.description,
          popular: packForm.popular,
          active: packForm.active,
          sortOrder: parseInt(packForm.sortOrder) || 0
        });
        toast.success('Credit pack updated');
      } else {
        if (!packForm.packId) {
          toast.error('Pack ID is required for new packs');
          return;
        }
        await api.post('/admin/credit-packs', {
          packId: packForm.packId,
          name: packForm.name,
          credits: parseInt(packForm.credits),
          price: parseFloat(packForm.price),
          description: packForm.description,
          popular: packForm.popular,
          sortOrder: parseInt(packForm.sortOrder) || 0
        });
        toast.success('Credit pack created');
      }
      
      // Refresh packs
      const res = await api.get('/admin/credit-packs');
      setCreditPacks(res.data.packs);
      setShowPackForm(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save credit pack');
    }
  };

  const handleDeletePack = async (pack) => {
    if (!window.confirm(`Delete "${pack.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/admin/credit-packs/${pack.id}`);
      toast.success('Credit pack deleted');
      setCreditPacks(creditPacks.filter(p => p.id !== pack.id));
    } catch (error) {
      toast.error('Failed to delete credit pack');
    }
  };

  // ==================== PROMO CODE MANAGEMENT ====================
  const handleAddPromo = () => {
    setEditingPromo(null);
    setPromoForm({ code: '', credits: '', maxUses: '', expiresAt: '', description: '', active: true });
    setShowPromoForm(true);
  };

  const handleEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      code: promo.code,
      credits: promo.credits.toString(),
      maxUses: promo.max_uses?.toString() || '',
      expiresAt: promo.expires_at ? promo.expires_at.split('T')[0] : '',
      description: promo.description || '',
      active: !!promo.active
    });
    setShowPromoForm(true);
  };

  const handleSavePromo = async () => {
    if (!promoForm.code || !promoForm.credits) {
      toast.error('Code and credits are required');
      return;
    }

    try {
      const payload = {
        code: promoForm.code.toUpperCase(),
        credits: parseInt(promoForm.credits),
        maxUses: promoForm.maxUses ? parseInt(promoForm.maxUses) : null,
        expiresAt: promoForm.expiresAt || null,
        description: promoForm.description,
        active: promoForm.active
      };

      if (editingPromo) {
        await api.put(`/admin/promo-codes/${editingPromo.id}`, payload);
        toast.success('Promo code updated');
      } else {
        await api.post('/admin/promo-codes', payload);
        toast.success('Promo code created');
      }
      
      // Refresh promo codes
      const res = await api.get('/admin/promo-codes');
      setPromoCodes(res.data.codes);
      setShowPromoForm(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save promo code');
    }
  };

  const handleDeletePromo = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/admin/promo-codes/${promo.id}`);
      toast.success('Promo code deleted');
      setPromoCodes(promoCodes.filter(p => p.id !== promo.id));
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  if (!user?.isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="hero" style={{ padding: '30px 20px' }}>
        <h1 className="hero-title" style={{ fontSize: '2.5rem' }}>
          🔧 Admin Dashboard
        </h1>
        <p className="hero-subtitle">
          Manage users, view stats, and monitor messages
        </p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid" style={{ marginBottom: '30px' }}>
        <div className="stat-card">
          <div className="stat-value">{stats?.totalUsers || 0}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.totalCredits || 0}</div>
          <div className="stat-label">Total Credits</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats?.totalRevenue)}</div>
          <div className="stat-label">Revenue</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.completedPurchases || 0}</div>
          <div className="stat-label">Purchases</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.sentMessages || 0}</div>
          <div className="stat-label">Messages Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.totalMessages || 0}</div>
          <div className="stat-label">Total Messages</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: '20px' }}>
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          👥 Users ({users.length})
        </button>
        <button 
          className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          💬 Recent Messages
        </button>
        <button 
          className={`tab ${activeTab === 'packs' ? 'active' : ''}`}
          onClick={() => setActiveTab('packs')}
        >
          💰 Credit Packs
        </button>
        <button 
          className={`tab ${activeTab === 'promos' ? 'active' : ''}`}
          onClick={() => setActiveTab('promos')}
        >
          🎁 Promo Codes
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="card">
          <h2 className="card-title">📈 Quick Stats</h2>
          <div style={{ display: 'grid', gap: '15px' }}>
            <div style={{ padding: '15px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
              <strong>Average Credits per User:</strong>{' '}
              {stats?.totalUsers > 0 ? (stats.totalCredits / stats.totalUsers).toFixed(1) : 0}
            </div>
            <div style={{ padding: '15px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
              <strong>Average Revenue per Purchase:</strong>{' '}
              {formatCurrency(stats?.completedPurchases > 0 ? stats.totalRevenue / stats.completedPurchases : 0)}
            </div>
            <div style={{ padding: '15px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
              <strong>Message Delivery Rate:</strong>{' '}
              {stats?.totalMessages > 0 ? ((stats.sentMessages / stats.totalMessages) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="card">
          <h2 className="card-title">👥 User Management</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Credits</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Custom Sender</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Admin</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Joined</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px' }}>{u.name}</td>
                    <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {editingUser?.id === u.id ? (
                        <input
                          type="number"
                          value={newCredits}
                          onChange={(e) => setNewCredits(e.target.value)}
                          style={{ 
                            width: '80px', 
                            padding: '5px', 
                            textAlign: 'center',
                            background: 'var(--bg-input)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '5px',
                            color: 'var(--text-primary)'
                          }}
                          min="0"
                          autoFocus
                        />
                      ) : (
                        <span style={{ 
                          background: 'var(--christmas-green)', 
                          padding: '4px 12px', 
                          borderRadius: '12px',
                          fontSize: '0.9rem'
                        }}>
                          {u.credits}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggleSenderId(u)}
                        className={`btn ${u.senderIdUnlocked ? 'btn-primary' : 'btn-outline'}`}
                        style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                        title={u.senderIdUnlocked ? 'Click to disable' : 'Click to enable'}
                      >
                        {u.senderIdUnlocked ? '✅ On' : 'Off'}
                      </button>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {u.isAdmin ? '✅' : '—'}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      {formatDate(u.createdAt)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {editingUser?.id === u.id ? (
                        <>
                          <button 
                            onClick={handleSaveCredits}
                            className="btn btn-primary"
                            style={{ padding: '5px 10px', fontSize: '0.8rem', marginRight: '5px' }}
                          >
                            Save
                          </button>
                          <button 
                            onClick={handleCancelEdit}
                            className="btn btn-outline"
                            style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button 
                          onClick={() => handleEditCredits(u)}
                          className="btn btn-outline"
                          style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                        >
                          Edit Credits
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="card">
          <h2 className="card-title">💬 Last 25 Messages</h2>
          {messages.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>No messages yet</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>User</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Sender</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Recipient</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Message</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map(msg => (
                    <tr key={msg.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px' }}>
                        <div>{msg.user_name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{msg.user_email}</div>
                      </td>
                      <td style={{ padding: '12px' }}>{msg.sender_id}</td>
                      <td style={{ padding: '12px', fontFamily: 'monospace' }}>{msg.recipient_phone}</td>
                      <td style={{ padding: '12px', maxWidth: '200px' }} title={msg.content}>
                        {truncateMessage(msg.content)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          background: msg.status === 'sent' ? 'var(--christmas-green)' : 
                                      msg.status === 'scheduled' ? 'var(--christmas-gold)' :
                                      msg.status === 'failed' ? 'var(--christmas-red)' : 
                                      'var(--text-secondary)',
                          color: 'white'
                        }}>
                          {msg.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        {formatDate(msg.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Credit Packs Tab */}
      {activeTab === 'packs' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>💰 Credit Packs</h2>
            <button className="btn btn-primary" onClick={handleAddPack}>
              + Add Pack
            </button>
          </div>

          {showPackForm && (
            <div style={{ 
              background: 'rgba(255,255,255,0.05)', 
              padding: '20px', 
              borderRadius: '10px', 
              marginBottom: '20px' 
            }}>
              <h3 style={{ marginBottom: '15px' }}>{editingPack ? 'Edit Pack' : 'New Pack'}</h3>
              <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                {!editingPack && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Pack ID (unique)</label>
                    <input
                      type="text"
                      className="input"
                      value={packForm.packId}
                      onChange={(e) => setPackForm({...packForm, packId: e.target.value})}
                      placeholder="e.g., starter"
                    />
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Name</label>
                  <input
                    type="text"
                    className="input"
                    value={packForm.name}
                    onChange={(e) => setPackForm({...packForm, name: e.target.value})}
                    placeholder="e.g., Starter Pack"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Credits</label>
                  <input
                    type="number"
                    className="input"
                    value={packForm.credits}
                    onChange={(e) => setPackForm({...packForm, credits: e.target.value})}
                    placeholder="10"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Price (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input"
                    value={packForm.price}
                    onChange={(e) => setPackForm({...packForm, price: e.target.value})}
                    placeholder="4.99"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Description</label>
                  <input
                    type="text"
                    className="input"
                    value={packForm.description}
                    onChange={(e) => setPackForm({...packForm, description: e.target.value})}
                    placeholder="Perfect for trying out"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Sort Order</label>
                  <input
                    type="number"
                    className="input"
                    value={packForm.sortOrder}
                    onChange={(e) => setPackForm({...packForm, sortOrder: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={packForm.popular}
                      onChange={(e) => setPackForm({...packForm, popular: e.target.checked})}
                    />
                    Popular
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={packForm.active}
                      onChange={(e) => setPackForm({...packForm, active: e.target.checked})}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button className="btn btn-primary" onClick={handleSavePack}>Save</button>
                <button className="btn btn-outline" onClick={() => setShowPackForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Pack ID</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Credits</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Price</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Popular</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Active</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {creditPacks.map(pack => (
                  <tr key={pack.id} style={{ borderBottom: '1px solid var(--border-color)', opacity: pack.active ? 1 : 0.5 }}>
                    <td style={{ padding: '12px', fontFamily: 'monospace' }}>{pack.pack_id}</td>
                    <td style={{ padding: '12px', fontWeight: 'bold' }}>{pack.name}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{pack.credits}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>£{pack.price.toFixed(2)}</td>
                    <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>{pack.description}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{pack.popular ? '⭐' : '-'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{pack.active ? '✅' : '❌'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-outline" 
                          style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                          onClick={() => handleEditPack(pack)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-outline" 
                          style={{ padding: '5px 10px', fontSize: '0.8rem', color: 'var(--christmas-red)' }}
                          onClick={() => handleDeletePack(pack)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Promo Codes Tab */}
      {activeTab === 'promos' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="card-title" style={{ margin: 0 }}>🎁 Promo Codes</h2>
            <button className="btn btn-primary" onClick={handleAddPromo}>
              + Add Promo
            </button>
          </div>

          {showPromoForm && (
            <div style={{ 
              background: 'rgba(255,255,255,0.05)', 
              padding: '20px', 
              borderRadius: '10px', 
              marginBottom: '20px' 
            }}>
              <h3 style={{ marginBottom: '15px' }}>{editingPromo ? 'Edit Promo Code' : 'New Promo Code'}</h3>
              <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Code</label>
                  <input
                    type="text"
                    className="input"
                    value={promoForm.code}
                    onChange={(e) => setPromoForm({...promoForm, code: e.target.value.toUpperCase()})}
                    placeholder="e.g., SANTA2024"
                    disabled={!!editingPromo}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Credits</label>
                  <input
                    type="number"
                    className="input"
                    value={promoForm.credits}
                    onChange={(e) => setPromoForm({...promoForm, credits: e.target.value})}
                    placeholder="5"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Max Uses (blank=unlimited)</label>
                  <input
                    type="number"
                    className="input"
                    value={promoForm.maxUses}
                    onChange={(e) => setPromoForm({...promoForm, maxUses: e.target.value})}
                    placeholder="100"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Expires (optional)</label>
                  <input
                    type="date"
                    className="input"
                    value={promoForm.expiresAt}
                    onChange={(e) => setPromoForm({...promoForm, expiresAt: e.target.value})}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.9rem' }}>Description</label>
                  <input
                    type="text"
                    className="input"
                    value={promoForm.description}
                    onChange={(e) => setPromoForm({...promoForm, description: e.target.value})}
                    placeholder="Launch promo for social media"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={promoForm.active}
                      onChange={(e) => setPromoForm({...promoForm, active: e.target.checked})}
                    />
                    Active
                  </label>
                </div>
              </div>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button className="btn btn-primary" onClick={handleSavePromo}>Save</button>
                <button className="btn btn-outline" onClick={() => setShowPromoForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Code</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Credits</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Usage</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Expires</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Active</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map(promo => (
                  <tr key={promo.id} style={{ borderBottom: '1px solid var(--border-color)', opacity: promo.active ? 1 : 0.5 }}>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 'bold' }}>{promo.code}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{promo.credits}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {promo.used_count || 0}{promo.max_uses ? ` / ${promo.max_uses}` : ' / ∞'}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                      {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>{promo.description || '-'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>{promo.active ? '✅' : '❌'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button 
                          className="btn btn-outline" 
                          style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                          onClick={() => handleEditPromo(promo)}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-outline" 
                          style={{ padding: '5px 10px', fontSize: '0.8rem', color: 'var(--christmas-red)' }}
                          onClick={() => handleDeletePromo(promo)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {promoCodes.length === 0 && (
                  <tr>
                    <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      No promo codes yet. Click "+ Add Promo" to create one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
